/**
 * WorkPlanDetail — control global de un plan, a PANTALLA COMPLETA.
 *
 * Es la pantalla que responde «cómo va esto en todo el parque». Cada fila es un
 * elemento (estación, servidor, VM…) y cada columna un paso; la celda dice el
 * estado, **desde cuándo** se cumple y, si lo marcó alguien a mano, **quién**.
 *
 * Tres tipos de paso:
 *  · `inventory` — lo decide el inventario. No se puede marcar… salvo que el
 *    dato no exista y el paso lleve `allow_manual`: entonces sí, y se respeta
 *    mientras siga sin haber dato.
 *  · `manual`    — un clic lo marca; se guarda el nombre de quien lo hizo.
 *  · `command`   — un clic lanza el comando al agente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  NoSymbolIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  BookmarkIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import Dialog from './common/Dialog';
import HelpDot from '../academy/HelpDot';
import api from '../services/api';
import './WorkPlans.css';

const STATE_META = {
  done:    { icon: CheckCircleIcon, cls: 'wp-cell--done',    label: 'Hecho' },
  pending: { icon: MinusCircleIcon, cls: 'wp-cell--pending', label: 'Pendiente' },
  failed:  { icon: XCircleIcon,     cls: 'wp-cell--failed',  label: 'Fallido' },
  skipped: { icon: NoSymbolIcon,    cls: 'wp-cell--skipped', label: 'Omitido' },
};

const OPERADORES = [
  { value: 'eq', label: 'igual a' },
  { value: 'ne', label: 'distinto de' },
  { value: 'gte', label: 'mayor o igual que' },
  { value: 'lte', label: 'menor o igual que' },
  { value: 'contains', label: 'contiene' },
  { value: 'truthy', label: 'sí / activo' },
  { value: 'falsy', label: 'no / desactivado' },
  { value: 'exists', label: 'tiene valor' },
];

function fmtDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' });
}

function fmtDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** ¿Se puede pulsar esta celda, y qué hace al pulsarla? */
function cellAction(step, cell) {
  if (step.check_type === 'manual') return 'mark';
  if (step.check_type === 'command') return 'run';
  if (step.check_type === 'inventory' && step.allow_manual) {
    const sinDato = (cell?.evidence || {}).observed == null;
    return sinDato ? 'mark' : 'none';
  }
  return 'none';
}

function cellTitle(step, cell) {
  const partes = [step.title];
  partes.push(STATE_META[cell?.state]?.label || 'Pendiente');

  const ev = cell?.evidence || {};
  if (ev.field) {
    partes.push(`observado: ${ev.observed == null ? 'sin dato' : ev.observed}`);
    if (ev.op) partes.push(`esperado ${ev.op} ${ev.expected}`);
  }
  if (cell?.state === 'done' && cell?.changed_at) {
    partes.push(`desde ${fmtDateTime(cell.changed_at)}`);
  }
  if (cell?.marked_by) partes.push(`marcado por ${cell.marked_by}`);

  const accion = cellAction(step, cell);
  if (accion === 'mark') partes.push('clic para marcar/desmarcar');
  if (accion === 'run') partes.push('clic para lanzar el comando');
  return partes.join(' · ');
}

export default function WorkPlanDetail({ companyId }) {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [aviso, setAviso] = useState(null);

  const [pasoOpen, setPasoOpen] = useState(false);
  const [pasoEditando, setPasoEditando] = useState(null);
  const [pasoError, setPasoError] = useState('');
  const [pasoBusy, setPasoBusy] = useState(false);
  const [pasoForm, setPasoForm] = useState({
    title: '', description: '', check_type: 'manual', field: '', op: 'eq', value: '',
    allow_manual: false, doc_page_id: '',
  });
  const [pasoBorrar, setPasoBorrar] = useState(null);
  const [guardandoPlantilla, setGuardandoPlantilla] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState('');
  const [filtro, setFiltro] = useState('');
  // Por defecto solo se ven los elementos a los que les falta algo: abrir un
  // plan renove con 300 equipos ya hechos escondía los 12 que hay que tocar.
  const [soloPendientes, setSoloPendientes] = useState(true);

  // Documentos de la empresa para vincular a un paso («cómo se hace»).
  const [docs, setDocs] = useState([]);
  const [docTitulo, setDocTitulo] = useState('');
  const [docBusy, setDocBusy] = useState(false);

  // Alta de equipos desde el plan: se enseña el código que se asignará y se
  // crea con código = nombre, que es como se identifica el equipo.
  const [elementosOpen, setElementosOpen] = useState(false);
  const [elementosCantidad, setElementosCantidad] = useState(1);
  const [elementosCodigos, setElementosCodigos] = useState([]);
  const [elementosPreviewBusy, setElementosPreviewBusy] = useState(false);
  const [elementosTipo, setElementosTipo] = useState('pc');
  const [elementosUbicacion, setElementosUbicacion] = useState('');
  const [elementosBusy, setElementosBusy] = useState(false);
  const [elementosError, setElementosError] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);

  const [alcanceOpen, setAlcanceOpen] = useState(false);
  const [alcanceTipos, setAlcanceTipos] = useState([]);
  const [alcanceSoloLista, setAlcanceSoloLista] = useState(false);
  const [alcanceIds, setAlcanceIds] = useState({});
  const [alcanceTargets, setAlcanceTargets] = useState([]);
  const [alcanceBusy, setAlcanceBusy] = useState(false);
  // Condiciones del plan: portátil/sobremesa y condiciones libres sobre
  // inventario («fecha de compra ≤ 2020-01-01» para un plan renove).
  const [alcanceDeviceType, setAlcanceDeviceType] = useState('');
  const [alcanceCondiciones, setAlcanceCondiciones] = useState([]);

  const cargar = useCallback(async () => {
    if (!companyId || !planId) return;
    setError('');
    try {
      const res = await api.getWorkPlan(companyId, planId);
      setData(res);
      setNombrePlantilla(res?.plan?.title || '');
    } catch (e) {
      setError(e.message || 'No se pudo cargar el plan');
    } finally {
      setLoading(false);
    }
  }, [companyId, planId]);

  useEffect(() => { setLoading(true); cargar(); }, [cargar]);

  useEffect(() => {
    if (!companyId) return;
    api.getWorkPlanCatalog(companyId).then(setCatalogo).catch(() => setCatalogo([]));
  }, [companyId]);

  const cargarDocs = useCallback(async () => {
    if (!companyId) { setDocs([]); return; }
    try {
      setDocs(await api.getDocPages(companyId));
    } catch {
      setDocs([]);
    }
  }, [companyId]);

  useEffect(() => { cargarDocs(); }, [cargarDocs]);

  const plan = data?.plan;
  const progress = data?.progress;
  const rows = useMemo(() => data?.rows || [], [data]);
  const steps = useMemo(() => plan?.steps || [], [plan]);

  /** Campos ofrecidos para el tipo de elemento del plan. */
  const campos = useMemo(() => {
    const tipos = plan?.target_types || ['workstation'];
    const salida = [];
    const vistos = new Set();
    tipos.forEach((tipo) => {
      const info = catalogo.find((c) => c.key === tipo);
      (info?.fields || []).forEach((f) => {
        if (vistos.has(f.field)) return;
        vistos.add(f.field);
        salida.push({ ...f, tipoElemento: tipo });
      });
    });
    return salida;
  }, [catalogo, plan]);

  /** ¿Este elemento ya tiene todos los pasos hechos (u omitidos)? */
  const filaCompleta = useCallback((row) => (
    steps.length > 0 && steps.every((s) => {
      const cell = row.steps[String(s.id)];
      return cell && (cell.state === 'done' || cell.state === 'skipped');
    })
  ), [steps]);

  const pendientes = useMemo(() => rows.filter((r) => !filaCompleta(r)), [rows, filaCompleta]);

  const filasVisibles = useMemo(() => {
    const base = soloPendientes ? pendientes : rows;
    const q = filtro.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => `${r.name || ''} ${r.hostname || ''} ${r.type_label || ''} ${r.os_name || ''}`
      .toLowerCase().includes(q));
  }, [rows, pendientes, soloPendientes, filtro]);

  /** Campos disponibles para las condiciones del alcance (según los tipos). */
  const alcanceCampos = useMemo(() => {
    const tipos = alcanceTipos.length ? alcanceTipos : (plan?.target_types || ['workstation']);
    const salida = [];
    const vistos = new Set();
    tipos.forEach((tipo) => {
      const info = catalogo.find((c) => c.key === tipo);
      (info?.fields || []).forEach((f) => {
        if (vistos.has(f.field)) return;
        vistos.add(f.field);
        salida.push({ ...f, tipoElemento: tipo });
      });
    });
    return salida;
  }, [catalogo, alcanceTipos, plan]);

  /** Texto legible de las condiciones guardadas, para no tener que abrir el alcance. */
  const filtroResumen = useMemo(() => {
    const f = plan?.target_filter || {};
    const partes = [];
    if (f.device_type) {
      const tipo = String(f.device_type).toLowerCase();
      partes.push(['laptop', 'portatil', 'portátil'].includes(tipo) ? 'Solo portátiles' : 'Solo sobremesa');
    }
    if (f.os) partes.push(`SO: ${f.os}`);
    (f.conditions || []).forEach((c) => {
      if (!c?.field) return;
      const etiqueta = campos.find((x) => x.field === c.field)?.label || c.field;
      const operador = OPERADORES.find((o) => o.value === c.op)?.label || c.op;
      if (['exists', 'truthy', 'falsy'].includes(c.op)) partes.push(`${etiqueta} ${operador}`);
      else partes.push(`${etiqueta} ${operador} ${c.value}`);
    });
    return partes;
  }, [plan, campos]);

  const recalcular = async () => {
    setBusy('evaluate');
    try {
      await api.evaluateWorkPlan(companyId, planId);
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo recalcular');
    } finally {
      setBusy('');
    }
  };

  const abrirPaso = () => {
    setPasoError('');
    setPasoEditando(null);
    setPasoForm({
      title: '', description: '', check_type: 'manual', field: '', op: 'eq', value: '',
      allow_manual: false, doc_page_id: '',
    });
    setDocTitulo('');
    setPasoOpen(true);
  };

  /** Abre el mismo formulario con los datos del paso, para corregirlo. */
  const editarPaso = (step) => {
    const config = step.check_config || {};
    setPasoError('');
    setPasoEditando(step);
    setPasoForm({
      title: step.title || '',
      description: step.description || '',
      check_type: step.check_type || 'manual',
      field: config.field || '',
      op: config.op || 'eq',
      value: config.value == null ? '' : String(config.value),
      allow_manual: Boolean(step.allow_manual),
      doc_page_id: step.doc_page_id || '',
    });
    setDocTitulo('');
    setPasoOpen(true);
  };

  /** Crea un documento en borrador y lo deja vinculado al paso que se edita. */
  const crearDocPaso = async () => {
    const titulo = docTitulo.trim();
    if (!titulo) return;
    setDocBusy(true);
    setPasoError('');
    try {
      const doc = await api.createDocPage(companyId, {
        title: titulo,
        description: `Procedimiento del paso «${pasoForm.title || 'sin título'}» de un plan de trabajo.`,
        tags: ['planes'],
        status: 'draft',
      });
      setDocs((prev) => [doc, ...prev]);
      setPasoForm((prev) => ({ ...prev, doc_page_id: doc.id }));
      setDocTitulo('');
    } catch (e) {
      setPasoError(e.message || 'No se pudo crear el documento');
    } finally {
      setDocBusy(false);
    }
  };

  const guardarPaso = async () => {
    if (!pasoForm.title.trim()) { setPasoError('El paso necesita un título'); return; }
    const payload = {
      title: pasoForm.title.trim(),
      description: pasoForm.description.trim() || null,
      check_type: pasoForm.check_type,
      allow_manual: pasoForm.allow_manual,
      doc_page_id: pasoForm.doc_page_id || null,
    };
    if (pasoForm.check_type === 'inventory') {
      if (!pasoForm.field) { setPasoError('Elige el campo de inventario a comprobar'); return; }
      payload.check_config = { field: pasoForm.field, op: pasoForm.op || 'eq', value: pasoForm.value };
    }
    setPasoBusy(true);
    try {
      if (pasoEditando) {
        await api.updateWorkPlanStep(companyId, pasoEditando.id, payload);
      } else {
        await api.addWorkPlanStep(companyId, planId, payload);
      }
      setPasoOpen(false);
      setPasoEditando(null);
      await cargar();
    } catch (e) {
      setPasoError(e.message || 'No se pudo guardar el paso');
    } finally {
      setPasoBusy(false);
    }
  };

  /** Mueve una columna (un paso) una posición a izquierda o derecha. */
  const moverPaso = async (step, delta) => {
    const ids = steps.map((s) => s.id);
    const desde = ids.indexOf(step.id);
    const hasta = desde + delta;
    if (desde < 0 || hasta < 0 || hasta >= ids.length) return;
    [ids[desde], ids[hasta]] = [ids[hasta], ids[desde]];
    setBusy(`move-${step.id}`);
    try {
      await api.reorderWorkPlanSteps(companyId, planId, ids);
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar el orden de los pasos');
    } finally {
      setBusy('');
    }
  };

  /** Pide a nomenclatura los próximos códigos (sin consumir el contador). */
  const cargarPreviewElementos = async (cantidad, ubicacion) => {
    setElementosPreviewBusy(true);
    setElementosError('');
    try {
      const codigos = await api.previewNamingCodes(companyId, {
        type: 'workstation',
        count: Math.max(1, Math.min(Number(cantidad) || 1, 100)),
        locationId: ubicacion || null,
      });
      setElementosCodigos(codigos);
    } catch (e) {
      setElementosCodigos([]);
      setElementosError(e.message || 'No se pudieron calcular los códigos');
    } finally {
      setElementosPreviewBusy(false);
    }
  };

  /** Abre el alta rápida de equipos desde el plan. */
  const abrirElementos = async () => {
    setElementosError('');
    setElementosCantidad(1);
    setElementosCodigos([]);
    const filtroPlan = plan?.target_filter || {};
    const tipo = String(filtroPlan.device_type || '').toLowerCase();
    setElementosTipo(['laptop', 'portatil', 'portátil'].includes(tipo) ? 'laptop' : 'pc');
    setElementosUbicacion('');
    setElementosOpen(true);
    try {
      const res = await api.getLocations('', companyId);
      setUbicaciones(res?.data || []);
    } catch {
      setUbicaciones([]);
    }
    cargarPreviewElementos(1, '');
  };

  const crearElementos = async () => {
    const codigos = elementosCodigos.map((codigo) => (codigo || '').trim()).filter(Boolean);
    if (!codigos.length) { setElementosError('No hay códigos que crear'); return; }
    setElementosBusy(true);
    setElementosError('');
    try {
      const res = await api.createWorkPlanElements(companyId, planId, {
        codes: codigos,
        device_type: elementosTipo,
        location_id: elementosUbicacion ? Number(elementosUbicacion) : null,
      });
      const creados = res.created || [];
      if (!creados.length) {
        setElementosError('El servidor no ha creado ningún equipo. Revisa los códigos e inténtalo de nuevo.');
        return;
      }
      // Se cierra el diálogo y se confirma: antes se quedaba abierto, sin lista
      // visible (queda bajo el pliegue) y con el botón deshabilitado.
      setElementosOpen(false);
      await cargar();
      setAviso({
        title: creados.length === 1 ? 'Equipo creado' : `${creados.length} equipos creados`,
        message: `${creados.map((equipo) => equipo.code).join(', ')}. `
          + 'Ya están en la matriz con las tareas pendientes; el agente se instala desde la ficha de cada equipo. '
          + 'Si el plan tiene condiciones (por ejemplo, fecha de compra), aparecerán cuando las cumplan.',
      });
    } catch (e) {
      setElementosError(e.message || 'No se pudieron crear los equipos');
    } finally {
      setElementosBusy(false);
    }
  };

  const borrarPaso = async (step) => {
    if (!step) return;
    try {
      await api.deleteWorkPlanStep(companyId, step.id);
      setPasoBorrar(null);
      await cargar();
    } catch (e) {
      setPasoBorrar(null);
      setError(e.message || 'No se pudo borrar el paso');
    }
  };

  const guardarPlantilla = async () => {
    try {
      await api.saveWorkPlanAsTemplate(companyId, planId, { title: nombrePlantilla.trim() });
      setGuardandoPlantilla(false);
      setAviso({
        title: 'Plantilla guardada',
        message: 'Ya está disponible en «Nuevo plan» para el próximo lote de equipos.',
      });
    } catch (e) {
      setError(e.message || 'No se pudo guardar la plantilla');
    }
  };

  /** Abre el alcance del plan: a qué tipos aplica y a qué elementos concretos. */
  const abrirAlcance = async () => {
    const tipos = plan?.target_types || ['workstation'];
    const ids = new Set((plan?.target_ids || []).map((r) => `${r.ci_type}:${r.ci_id}`));
    const filtroPlan = plan?.target_filter || {};
    setAlcanceTipos(tipos);
    setAlcanceSoloLista(ids.size > 0);
    setAlcanceIds(Object.fromEntries([...ids].map((k) => [k, true])));
    setAlcanceDeviceType(filtroPlan.device_type || '');
    setAlcanceCondiciones(
      (Array.isArray(filtroPlan.conditions) ? filtroPlan.conditions : []).map((c) => ({
        field: c.field || '',
        op: c.op || 'eq',
        value: c.value == null ? '' : String(c.value),
      })),
    );
    setAlcanceOpen(true);
    setAlcanceBusy(true);
    try {
      setAlcanceTargets(await api.getWorkPlanTargets(companyId, tipos));
    } catch {
      setAlcanceTargets([]);
    } finally {
      setAlcanceBusy(false);
    }
  };

  const cambiarTipos = async (nuevos) => {
    setAlcanceTipos(nuevos);
    setAlcanceBusy(true);
    try {
      setAlcanceTargets(await api.getWorkPlanTargets(companyId, nuevos.length ? nuevos : ['workstation']));
    } catch {
      setAlcanceTargets([]);
    } finally {
      setAlcanceBusy(false);
    }
  };

  const guardarAlcance = async () => {
    if (!alcanceTipos.length) { setError('Elige al menos un tipo de elemento'); return; }
    const target_ids = alcanceSoloLista
      ? Object.keys(alcanceIds)
        .filter((k) => alcanceIds[k])
        .map((k) => {
          const [ci_type, ci_id] = k.split(':');
          return { ci_type, ci_id: Number(ci_id) };
        })
      : [];
    if (alcanceSoloLista && target_ids.length === 0) {
      setError('Marca al menos un elemento, o desactiva «solo estos»');
      return;
    }
    // Se conserva lo que no se edita aquí (`os` de plantillas antiguas) y se
    // reescriben solo el tipo de equipo y las condiciones.
    const target_filter = { ...(plan?.target_filter || {}) };
    delete target_filter.device_type;
    delete target_filter.conditions;
    if (alcanceDeviceType) target_filter.device_type = alcanceDeviceType;
    const condiciones = alcanceCondiciones
      .filter((c) => c.field)
      .map((c) => ({
        field: c.field,
        op: c.op || 'eq',
        value: ['exists', 'truthy', 'falsy'].includes(c.op) ? null : c.value,
      }));
    if (condiciones.length) target_filter.conditions = condiciones;

    try {
      await api.updateWorkPlan(companyId, planId, {
        target_types: alcanceTipos,
        target_ids,
        target_filter,
      });
      setAlcanceOpen(false);
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar el alcance');
    }
  };

  const accionCelda = async (step, row, cell) => {
    if (!companyId) return;
    const clave = `${step.id}-${row.ci_type}-${row.ci_id}`;
    const accion = cellAction(step, cell);
    if (accion === 'none') return;

    setBusy(clave);
    try {
      if (accion === 'mark') {
        const nuevo = cell?.state === 'done' ? 'pending' : 'done';
        await api.markWorkPlanStep(companyId, step.id, {
          ci_type: row.ci_type, ci_id: row.ci_id, state: nuevo,
        });
      } else {
        await api.runWorkPlanStep(companyId, step.id, {
          ci_type: row.ci_type, ci_id: row.ci_id,
        });
        setAviso({
          title: 'Comando enviado',
          message: `Se ha encolado «${step.command?.command || 'comando'}» para ${row.name || row.ci_id}. `
            + 'El resultado aparecerá en la pestaña Agente del elemento.',
        });
      }
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo actualizar el paso');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="wp-page">
      <header className="wp-topbar">
        <div className="wp-topbar-left">
          <button type="button" className="wp-btn" onClick={() => navigate('/work-plans')}>
            <ArrowLeftIcon /> Volver
          </button>
          <div className="wp-miga">
            <span>Planes de trabajo</span>
            <span className="wp-miga-sep">/</span>
            <strong>{plan?.title || '…'}</strong>
          </div>
          <HelpDot topic="planes.concept" label="Ayuda: qué es un plan de trabajo" />
        </div>
        <div className="wp-topbar-right">
          <button type="button" className="wp-btn" onClick={abrirAlcance} title="A qué elementos aplica este plan">
            <AdjustmentsHorizontalIcon /> Alcance
          </button>
          <button type="button" className="wp-btn" onClick={recalcular} disabled={busy === 'evaluate'}>
            <ArrowPathIcon /> Recalcular
          </button>
          {(plan?.target_types || []).includes('workstation') && (
            <button
              type="button"
              className="wp-btn"
              onClick={abrirElementos}
              title="Ver los códigos que se asignarán y crear los equipos (código = nombre)"
            >
              <ComputerDesktopIcon /> Crear equipos
            </button>
          )}
          <button type="button" className="wp-btn" onClick={abrirPaso}>
            <PlusIcon /> Añadir paso
          </button>
          <button type="button" className="wp-btn" onClick={() => setGuardandoPlantilla(true)}>
            <BookmarkIcon /> Guardar como plantilla
          </button>
        </div>
      </header>

      <div className="wp-content">
        {error && (
          <div className="wp-alert" role="alert">
            <ExclamationTriangleIcon />
            <span>{error}</span>
          </div>
        )}

        {loading && <div className="list-hint">Cargando el plan…</div>}

        {!loading && plan && (
          <>
            {plan.description && <p className="wp-plan-desc">{plan.description}</p>}

            {filtroResumen.length > 0 && (
              <div className="wp-filter-note">
                <span className="wp-filter-note-label">Condiciones del plan</span>
                {filtroResumen.map((texto) => (
                  <span key={texto} className="wp-filter-chip">{texto}</span>
                ))}
              </div>
            )}

            <div className="wp-summary">
              <div className="wp-summary-card">
                <div className="wp-summary-label">Elementos afectados</div>
                <div className="wp-summary-value">{progress?.elementos ?? 0}</div>
                <div className="wp-summary-hint">
                  {progress?.pasos ?? 0} pasos por elemento
                  {plan.target_ids?.length ? ' · lista concreta' : ''}
                </div>
              </div>
              <div className="wp-summary-card">
                <div className="wp-summary-label">Avance</div>
                <div className="wp-summary-value">{progress?.porcentaje ?? 0}%</div>
                <div className="wp-progress">
                  <div className="wp-progress-bar">
                    <div
                      className={`wp-progress-fill ${progress?.porcentaje === 100 ? 'is-done' : ''}`}
                      style={{ width: `${progress?.porcentaje ?? 0}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="wp-summary-card">
                <div className="wp-summary-label">Tareas hechas</div>
                <div className="wp-summary-value">
                  {progress?.hechos ?? 0}
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                    {' / '}{progress?.total ?? 0}
                  </span>
                </div>
                <div className="wp-summary-hint">
                  {progress?.elementos_completos ?? 0} elementos con todo hecho
                </div>
              </div>
              <div className="wp-summary-card">
                <div className="wp-summary-label">Tipo de elemento</div>
                <div className="wp-summary-value" style={{ fontSize: '0.95rem' }}>
                  {(plan.target_types || [])
                    .map((t) => catalogo.find((c) => c.key === t)?.label || t)
                    .join(' · ')}
                </div>
                <div className="wp-summary-hint">
                  {plan.created_by_name ? `Creado por ${plan.created_by_name}` : '—'}
                </div>
              </div>
            </div>

            <div className="wp-toolbar">
              <div className="wp-toolbar-left">
                <input
                  className="list-input wp-filter"
                  placeholder="Filtrar por equipo…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
                <button
                  type="button"
                  className={`wp-chip ${soloPendientes ? 'is-on' : ''}`}
                  onClick={() => setSoloPendientes((v) => !v)}
                  title={soloPendientes
                    ? 'Ahora solo se ven los elementos con tareas pendientes. Pulsa para verlos todos.'
                    : 'Ahora se ven todos los elementos. Pulsa para ver solo los que les faltan cosas.'}
                >
                  {soloPendientes ? 'Solo los que les faltan cosas' : 'Viendo todos los elementos'}
                </button>
                <span className="list-hint">
                  {pendientes.length} de {rows.length} con tareas pendientes
                </span>
              </div>
              <div className="wp-legend">
                <span><CheckCircleIcon style={{ color: 'var(--green-600, #16a34a)' }} /> Hecho</span>
                <span><MinusCircleIcon style={{ color: 'var(--text-muted)' }} /> Pendiente</span>
                <span><XCircleIcon style={{ color: 'var(--red-600, #dc2626)' }} /> Fallido</span>
                <span><NoSymbolIcon style={{ color: 'var(--text-muted)' }} /> Omitido</span>
                <span><PlayIcon style={{ color: 'var(--accent)' }} /> Un clic lanza el comando</span>
              </div>
            </div>

            <div className="wp-matrix">
              <table>
                <thead>
                  <tr>
                    <th className="wp-sticky-col">Elemento</th>
                    {steps.map((step, index) => (
                      <th key={step.id} title={step.description || step.title}>
                        <span className="wp-th-inner">
                          {step.title}
                          {step.doc && (
                            <a
                              className="wp-th-doc"
                              href={`/documentation/${step.doc.id}`}
                              target="_blank"
                              rel="noreferrer"
                              title={`Procedimiento: ${step.doc.title}`}
                            >
                              <DocumentTextIcon />
                            </a>
                          )}
                          <span className="wp-th-actions">
                            <button
                              type="button"
                              className="wp-cell"
                              style={{ padding: 2 }}
                              onClick={() => moverPaso(step, -1)}
                              disabled={index === 0 || busy === `move-${step.id}`}
                              title={`Mover «${step.title}» a la izquierda`}
                            >
                              <ChevronLeftIcon />
                            </button>
                            <button
                              type="button"
                              className="wp-cell"
                              style={{ padding: 2 }}
                              onClick={() => moverPaso(step, 1)}
                              disabled={index === steps.length - 1 || busy === `move-${step.id}`}
                              title={`Mover «${step.title}» a la derecha`}
                            >
                              <ChevronRightIcon />
                            </button>
                            <button
                              type="button"
                              className="wp-cell"
                              style={{ padding: 2 }}
                              onClick={() => editarPaso(step)}
                              title={`Editar el paso «${step.title}»`}
                            >
                              <PencilSquareIcon />
                            </button>
                            <button
                              type="button"
                              className="wp-cell"
                              style={{ padding: 2 }}
                              onClick={() => setPasoBorrar(step)}
                              title={`Quitar el paso «${step.title}»`}
                            >
                              <TrashIcon />
                            </button>
                          </span>
                        </span>
                        <div className="wp-th-kind">
                          {step.check_type === 'inventory' ? 'automático' : step.check_type === 'command' ? 'comando' : 'manual'}
                          {step.allow_manual && ' · se puede marcar'}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filasVisibles.length === 0 && (
                    <tr>
                      <td colSpan={steps.length + 1} className="list-hint">
                        {rows.length === 0
                          ? 'Ningún elemento de la empresa encaja con este plan.'
                          : soloPendientes && pendientes.length === 0
                            ? 'Todo hecho: ningún elemento tiene tareas pendientes.'
                            : 'Ningún elemento coincide con el filtro.'}
                      </td>
                    </tr>
                  )}
                  {filasVisibles.map((row) => (
                    <tr key={`${row.ci_type}-${row.ci_id}`}>
                      <td className="wp-sticky-col">
                        <div className="wp-device-name">{row.name || `${row.type_label} ${row.ci_id}`}</div>
                        <div className="wp-device-meta">
                          {[row.type_label, row.hostname, row.os_name, row.agent_version]
                            .filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      {steps.map((step) => {
                        const cell = row.steps[String(step.id)] || { state: 'pending' };
                        const meta = STATE_META[cell.state] || STATE_META.pending;
                        const Icono = meta.icon;
                        const accion = cellAction(step, cell);
                        return (
                          <td key={step.id}>
                            <button
                              type="button"
                              className={`wp-cell ${meta.cls}`}
                              disabled={accion === 'none' || busy === `${step.id}-${row.ci_type}-${row.ci_id}`}
                              onClick={() => accionCelda(step, row, cell)}
                              title={cellTitle(step, cell)}
                            >
                              <Icono />
                              {(cell.state === 'done' || cell.state === 'skipped') && cell.changed_at && (
                                <span className="wp-cell-date">{fmtDate(cell.changed_at)}</span>
                              )}
                              {cell.marked_by && (
                                <span className="wp-cell-who">{cell.marked_by}</span>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Añadir o editar paso ────────────────────────────────────────── */}
      {pasoOpen && (
        <Dialog
          title={pasoEditando ? 'Editar paso del plan' : 'Añadir paso al plan'}
          onClose={() => { setPasoOpen(false); setPasoEditando(null); }}
          maxWidth={540}
          busy={pasoBusy}
          footer={(
            <>
              <button
                type="button"
                className="wp-btn"
                onClick={() => { setPasoOpen(false); setPasoEditando(null); }}
                disabled={pasoBusy}
              >
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={guardarPaso} disabled={pasoBusy}>
                {pasoEditando ? 'Guardar' : 'Añadir'}
              </button>
            </>
          )}
        >
          {pasoError && <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>{pasoError}</div>}
          <label className="list-hint" htmlFor="wp-step-title">Qué hay que hacer</label>
          <input
            id="wp-step-title"
            className="list-input"
            value={pasoForm.title}
            onChange={(e) => setPasoForm({ ...pasoForm, title: e.target.value })}
            placeholder="Unir al dominio, cifrar el disco…"
            style={{ width: '100%', marginBottom: 10 }}
          />

          <label className="list-hint" htmlFor="wp-step-desc">Para qué sirve (opcional)</label>
          <textarea
            id="wp-step-desc"
            className="list-input"
            rows={2}
            value={pasoForm.description}
            onChange={(e) => setPasoForm({ ...pasoForm, description: e.target.value })}
            placeholder="Se ve al pasar el ratón por la columna"
            style={{ width: '100%', marginBottom: 10 }}
          />

          <label className="list-hint" htmlFor="wp-step-type">Cómo se da por hecho</label>
          <select
            id="wp-step-type"
            className="list-input"
            value={pasoForm.check_type}
            onChange={(e) => setPasoForm({ ...pasoForm, check_type: e.target.value })}
            style={{ width: '100%', marginBottom: 10 }}
          >
            <option value="manual">A mano (lo marca una persona)</option>
            <option value="inventory">Automático, mirando el inventario</option>
            {pasoForm.check_type === 'command' && (
              <option value="command">Comando al agente</option>
            )}
          </select>

          {pasoForm.check_type === 'inventory' && (
            <>
              <label className="list-hint" htmlFor="wp-step-field">Dato del elemento</label>
              <select
                id="wp-step-field"
                className="list-input"
                value={pasoForm.field}
                onChange={(e) => {
                  const campo = campos.find((c) => c.field === e.target.value);
                  setPasoForm({
                    ...pasoForm,
                    field: e.target.value,
                    op: campo?.kind === 'booleano' ? 'truthy' : 'eq',
                    value: '',
                  });
                }}
                style={{ width: '100%', marginBottom: 10 }}
              >
                <option value="">— elige el dato —</option>
                {campos.map((c) => (
                  <option key={c.field} value={c.field}>{c.label}</option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label className="list-hint" htmlFor="wp-step-op">Condición</label>
                  <select
                    id="wp-step-op"
                    className="list-input"
                    value={pasoForm.op}
                    onChange={(e) => setPasoForm({ ...pasoForm, op: e.target.value })}
                    style={{ width: '100%' }}
                  >
                    {OPERADORES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="list-hint" htmlFor="wp-step-value">Valor</label>
                  <input
                    id="wp-step-value"
                    className="list-input"
                    value={pasoForm.value}
                    onChange={(e) => setPasoForm({ ...pasoForm, value: e.target.value })}
                    disabled={['exists', 'truthy', 'falsy'].includes(pasoForm.op)}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={pasoForm.allow_manual}
                  onChange={(e) => setPasoForm({ ...pasoForm, allow_manual: e.target.checked })}
                  style={{ marginTop: 3 }}
                />
                <span className="list-hint" style={{ margin: 0 }}>
                  Si el inventario no trae el dato, se puede marcar a mano.
                  Útil para equipos recién llegados que aún no reportan.
                </span>
              </label>
            </>
          )}

          <div className="wp-doc-picker">
            <label className="list-hint" htmlFor="wp-step-doc">
              Documentación del paso (cómo se hace)
            </label>
            <select
              id="wp-step-doc"
              className="list-input"
              value={pasoForm.doc_page_id || ''}
              onChange={(e) => setPasoForm({
                ...pasoForm,
                doc_page_id: e.target.value ? Number(e.target.value) : '',
              })}
              style={{ width: '100%', marginBottom: 6 }}
            >
              <option value="">— sin documento —</option>
              {docs.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.title}{doc.status === 'draft' ? ' (borrador)' : ''}
                </option>
              ))}
            </select>

            {pasoForm.doc_page_id && (
              <a
                className="wp-doc-link"
                href={`/documentation/${pasoForm.doc_page_id}`}
                target="_blank"
                rel="noreferrer"
              >
                <ArrowTopRightOnSquareIcon /> Abrir el documento
              </a>
            )}

            <div className="wp-doc-create">
              <input
                className="list-input"
                value={docTitulo}
                onChange={(e) => setDocTitulo(e.target.value)}
                placeholder="O crea uno nuevo: «Cómo unir al dominio»"
              />
              <button
                type="button"
                className="wp-btn"
                onClick={crearDocPaso}
                disabled={docBusy || !docTitulo.trim()}
              >
                <PlusIcon /> {docBusy ? 'Creando…' : 'Crear y vincular'}
              </button>
            </div>
            <div className="list-hint">
              Se crea en borrador en la biblioteca de documentación; el paso solo lo
              enlaza para que cualquiera sepa cómo hacerlo.
            </div>
          </div>
        </Dialog>
      )}

      {pasoBorrar && (
        <Dialog
          title="Quitar paso"
          onClose={() => setPasoBorrar(null)}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setPasoBorrar(null)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={() => borrarPaso(pasoBorrar)}>Quitar</button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            ¿Quitar el paso <strong>{pasoBorrar.title}</strong>? Se borra su histórico
            en todos los elementos. Los elementos no se tocan.
          </p>
        </Dialog>
      )}

      {guardandoPlantilla && (
        <Dialog
          title="Guardar como plantilla"
          icon={<PencilSquareIcon />}
          onClose={() => setGuardandoPlantilla(false)}
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setGuardandoPlantilla(false)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={guardarPlantilla}>Guardar</button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Los pasos de este plan se guardan para reutilizarlos en el próximo lote.
            No se copia el estado de los equipos, solo la lista de tareas.
          </p>
          <label className="list-hint" htmlFor="wp-tpl-name">Nombre de la plantilla</label>
          <input
            id="wp-tpl-name"
            className="list-input"
            value={nombrePlantilla}
            onChange={(e) => setNombrePlantilla(e.target.value)}
            style={{ width: '100%' }}
          />
        </Dialog>
      )}

      {/* ── Alcance del plan ────────────────────────────────────────────── */}
      {alcanceOpen && (
        <Dialog
          title="Alcance del plan"
          icon={<AdjustmentsHorizontalIcon />}
          onClose={() => setAlcanceOpen(false)}
          maxWidth={620}
          busy={alcanceBusy}
          className="wp-alcance-dialog"
          footer={(
            <>
              <button type="button" className="wp-btn" onClick={() => setAlcanceOpen(false)}>Cancelar</button>
              <button type="button" className="list-btn-primary" onClick={guardarAlcance}>Aplicar</button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            A qué elementos se aplica. Por defecto, todos los del tipo elegido que
            encajen con el plan; si marcas <strong>solo estos</strong>, únicamente los
            que selecciones (los cinco equipos que acaban de llegar, por ejemplo).
          </p>

          <label className="list-hint">Tipo de elemento</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {catalogo.map((tipo) => {
              const activo = alcanceTipos.includes(tipo.key);
              return (
                <button
                  key={tipo.key}
                  type="button"
                  className={`wp-chip ${activo ? 'is-on' : ''}`}
                  onClick={() => {
                    const nuevos = activo
                      ? alcanceTipos.filter((t) => t !== tipo.key)
                      : [...alcanceTipos, tipo.key];
                    cambiarTipos(nuevos);
                  }}
                >
                  {tipo.plural}
                </button>
              );
            })}
          </div>

          {alcanceTipos.includes('workstation') && (
            <>
              <label className="list-hint">Tipo de equipo</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {[
                  { value: '', label: 'Todos' },
                  { value: 'laptop', label: 'Solo portátiles' },
                  { value: 'pc', label: 'Solo sobremesa' },
                ].map((opcion) => (
                  <button
                    key={opcion.value || 'todos'}
                    type="button"
                    className={`wp-chip ${alcanceDeviceType === opcion.value ? 'is-on' : ''}`}
                    onClick={() => setAlcanceDeviceType(opcion.value)}
                  >
                    {opcion.label}
                  </button>
                ))}
              </div>
            </>
          )}

          <label className="list-hint">Condiciones del plan</label>
          <div className="list-hint" style={{ marginTop: 0, marginBottom: 6 }}>
            Solo entran los elementos que las cumplan. Ejemplo de plan renove:
            <strong> fecha de compra · menor o igual que · 2020-01-01</strong>.
          </div>
          <div className="wp-conditions">
            {alcanceCondiciones.length === 0 && (
              <div className="list-hint">Sin condiciones: entran todos los elementos del tipo elegido.</div>
            )}
            {alcanceCondiciones.map((condicion, indice) => {
              const campo = alcanceCampos.find((c) => c.field === condicion.field);
              const sinValor = ['exists', 'truthy', 'falsy'].includes(condicion.op);
              return (
                <div key={`cond-${indice}`} className="wp-condition-row">
                  <select
                    className="list-input"
                    value={condicion.field}
                    onChange={(e) => {
                      const elegido = alcanceCampos.find((c) => c.field === e.target.value);
                      const op = elegido?.kind === 'booleano'
                        ? 'truthy'
                        : elegido?.kind === 'fecha' ? 'lte' : 'eq';
                      const copia = [...alcanceCondiciones];
                      copia[indice] = { ...condicion, field: e.target.value, op, value: '' };
                      setAlcanceCondiciones(copia);
                    }}
                    title="Dato del elemento"
                  >
                    <option value="">— dato —</option>
                    {alcanceCampos.map((c) => (
                      <option key={c.field} value={c.field}>{c.label}</option>
                    ))}
                  </select>
                  <select
                    className="list-input"
                    value={condicion.op}
                    onChange={(e) => {
                      const copia = [...alcanceCondiciones];
                      copia[indice] = { ...condicion, op: e.target.value };
                      setAlcanceCondiciones(copia);
                    }}
                    title="Condición"
                  >
                    {OPERADORES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <input
                    className="list-input"
                    type={campo?.kind === 'fecha' ? 'date' : campo?.kind === 'numero' ? 'number' : 'text'}
                    value={sinValor ? '' : condicion.value}
                    disabled={sinValor}
                    onChange={(e) => {
                      const copia = [...alcanceCondiciones];
                      copia[indice] = { ...condicion, value: e.target.value };
                      setAlcanceCondiciones(copia);
                    }}
                    placeholder="Valor"
                    title="Valor con el que se compara"
                  />
                  <button
                    type="button"
                    className="wp-icon-btn"
                    title="Quitar la condición"
                    onClick={() => setAlcanceCondiciones(
                      alcanceCondiciones.filter((_c, i) => i !== indice),
                    )}
                  >
                    <TrashIcon />
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="wp-btn"
              onClick={() => setAlcanceCondiciones([
                ...alcanceCondiciones,
                { field: '', op: 'eq', value: '' },
              ])}
            >
              <PlusIcon /> Añadir condición
            </button>
          </div>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={alcanceSoloLista}
              onChange={(e) => setAlcanceSoloLista(e.target.checked)}
            />
            <span className="list-hint" style={{ margin: 0 }}>Solo estos elementos</span>
          </label>

          {alcanceBusy && <div className="list-hint">Cargando elementos…</div>}

          {alcanceSoloLista && !alcanceBusy && (
            <div className="wp-target-list">
              {alcanceTargets.length === 0 && (
                <div className="list-hint">No hay elementos de los tipos elegidos.</div>
              )}
              {alcanceTargets.map((t) => {
                const clave = `${t.ci_type}:${t.ci_id}`;
                return (
                  <label key={clave} className="wp-target-row">
                    <input
                      type="checkbox"
                      checked={Boolean(alcanceIds[clave])}
                      onChange={(e) => {
                        const copia = { ...alcanceIds };
                        if (e.target.checked) copia[clave] = true;
                        else delete copia[clave];
                        setAlcanceIds(copia);
                      }}
                    />
                    <span className="wp-target-name">{t.name}</span>
                    <span className="wp-target-meta">{t.type_label}{t.meta ? ` · ${t.meta}` : ''}</span>
                  </label>
                );
              })}
            </div>
          )}
        </Dialog>
      )}

      {/* ── Alta rápida de equipos desde el plan ─────────────────────────── */}
      {elementosOpen && (
        <Dialog
          title="Crear equipos desde el plan"
          icon={<ComputerDesktopIcon />}
          onClose={() => setElementosOpen(false)}
          maxWidth={560}
          busy={elementosBusy}
          footer={(
            <>
              <button
                type="button"
                className="wp-btn"
                onClick={() => setElementosOpen(false)}
                disabled={elementosBusy}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={crearElementos}
                disabled={elementosBusy || elementosPreviewBusy || elementosCodigos.length === 0}
              >
                {elementosBusy ? 'Creando…' : 'Crear equipos'}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Para estos equipos <strong>el código es también el nombre</strong>. Dinos
            cuántos son y te enseñamos los códigos que asignará la nomenclatura;
            puedes corregir alguno antes de crear. El equipo nace con la ficha a
            medias: verás que le faltan el agente y los demás pasos del plan.
          </p>

          {elementosError && (
            <div className="list-hint" style={{ color: 'var(--red-600, #dc2626)' }}>
              {elementosError}
            </div>
          )}

          <label className="list-hint" htmlFor="wp-el-count">Cuántos equipos</label>
          <input
            id="wp-el-count"
            className="list-input"
            type="number"
            min={1}
            max={100}
            value={elementosCantidad}
            onChange={(e) => {
              const cantidad = Math.max(1, Math.min(Number(e.target.value) || 1, 100));
              setElementosCantidad(cantidad);
              cargarPreviewElementos(cantidad, elementosUbicacion);
            }}
            style={{ width: 120, marginBottom: 10 }}
          />

          <label className="list-hint">Códigos que se van a asignar</label>
          <div className="wp-code-list">
            {elementosPreviewBusy && (
              <div className="list-hint">Calculando los códigos…</div>
            )}
            {!elementosPreviewBusy && elementosCodigos.length === 0 && (
              <div className="list-hint">No se pudo proponer ningún código.</div>
            )}
            {elementosCodigos.map((codigo, indice) => (
              <input
                key={indice}
                className="list-input"
                value={codigo}
                onChange={(e) => {
                  const copia = [...elementosCodigos];
                  copia[indice] = e.target.value;
                  setElementosCodigos(copia);
                }}
                title="Puedes corregirlo antes de crear"
              />
            ))}
          </div>
          <div className="list-hint" style={{ marginBottom: 10 }}>
            Revisa los códigos: se crearán tal cual, con el mismo valor como nombre.
          </div>

          <label className="list-hint">Tipo de equipo</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {[
              { value: 'pc', label: 'Sobremesa' },
              { value: 'laptop', label: 'Portátil' },
            ].map((opcion) => (
              <button
                key={opcion.value}
                type="button"
                className={`wp-chip ${elementosTipo === opcion.value ? 'is-on' : ''}`}
                onClick={() => setElementosTipo(opcion.value)}
              >
                {opcion.label}
              </button>
            ))}
          </div>

          <label className="list-hint" htmlFor="wp-el-loc">Ubicación (opcional)</label>
          <select
            id="wp-el-loc"
            className="list-input"
            value={elementosUbicacion}
            onChange={(e) => {
              setElementosUbicacion(e.target.value);
              cargarPreviewElementos(elementosCantidad, e.target.value);
            }}
            style={{ width: '100%', marginBottom: 10 }}
          >
            <option value="">— sin ubicación —</option>
            {ubicaciones.map((ubicacion) => (
              <option key={ubicacion.id} value={ubicacion.id}>{ubicacion.name}</option>
            ))}
          </select>
        </Dialog>
      )}

      {aviso && (
        <Dialog
          title={aviso.title}
          onClose={() => setAviso(null)}
          footer={<button type="button" className="list-btn-primary" onClick={() => setAviso(null)}>Cerrar</button>}
        >
          <p style={{ margin: 0 }}>{aviso.message}</p>
        </Dialog>
      )}
    </div>
  );
}
